import React, { Suspense, lazy } from 'react';
import '@/App.css';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from '@/components/ui/sonner';
import { SiteProvider } from '@/context/SiteContext';
import { AuthProvider } from '@/context/AuthContext';
import { PublicLayout } from '@/components/public/PublicLayout';

// Public pages: the home page ships in the main bundle, every other route is code-split.
import HomePage from '@/pages/public/HomePage';
const StayPage = lazy(() => import('@/pages/public/StayPage'));
const DiningPage = lazy(() => import('@/pages/public/DiningPage'));
const ExperiencePage = lazy(() => import('@/pages/public/ExperiencePage'));
const JournalPage = lazy(() => import('@/pages/public/JournalPage'));
const JournalPostPage = lazy(() => import('@/pages/public/JournalPage').then((m) => ({ default: m.JournalPostPage })));
const GalleryPage = lazy(() => import('@/pages/public/GalleryPage'));
const ContactPage = lazy(() => import('@/pages/public/ContactPage'));
const GettingHerePage = lazy(() => import('@/pages/public/GettingHerePage'));
const OffersPage = lazy(() => import('@/pages/public/OffersPage'));
const NotFoundPage = lazy(() => import('@/pages/public/NotFoundPage'));

// Admin: one lazy chunk per screen so the public site never downloads CMS code.
const AdminLayout = lazy(() => import('@/components/admin/AdminLayout').then((m) => ({ default: m.AdminLayout })));
const AdminLogin = lazy(() => import('@/pages/admin/AdminLogin'));
const AdminDashboard = lazy(() => import('@/pages/admin/AdminDashboard'));
const AdminSettings = lazy(() => import('@/pages/admin/AdminSettings'));
const AdminHero = lazy(() => import('@/pages/admin/AdminHero'));
const AdminPages = lazy(() => import('@/pages/admin/AdminPages'));
const AdminPageEditor = lazy(() => import('@/pages/admin/AdminPages').then((m) => ({ default: m.AdminPageEditor })));
const AdminRooms = lazy(() => import('@/pages/admin/AdminRooms'));
const AdminDining = lazy(() => import('@/pages/admin/AdminDining'));
const AdminExperiences = lazy(() => import('@/pages/admin/AdminExperiences'));
const AdminJournal = lazy(() => import('@/pages/admin/AdminJournal'));
const AdminGallery = lazy(() => import('@/pages/admin/AdminGallery'));
const AdminReviews = lazy(() => import('@/pages/admin/AdminReviews'));
const AdminPromos = lazy(() => import('@/pages/admin/AdminPromos'));
const AdminMessages = lazy(() => import('@/pages/admin/AdminMessages'));
const AdminGettingHere = lazy(() => import('@/pages/admin/AdminGettingHere'));
const AdminOffers = lazy(() => import('@/pages/admin/AdminOffers'));
const AdminSubscribers = lazy(() => import('@/pages/admin/AdminSubscribers'));

const PageFallback = () => <div className="min-h-[60vh]" aria-hidden="true" />;
const AdminFallback = () => <div className="min-h-screen bg-secondary/40" aria-hidden="true" />;

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <SiteProvider>
            <Routes>
              <Route element={<PublicLayout />}>
                <Route path="/" element={<HomePage />} />
                <Route path="/stay" element={<Suspense fallback={<PageFallback />}><StayPage /></Suspense>} />
                <Route path="/dining" element={<Suspense fallback={<PageFallback />}><DiningPage /></Suspense>} />
                <Route path="/experience" element={<Suspense fallback={<PageFallback />}><ExperiencePage /></Suspense>} />
                <Route path="/journal" element={<Suspense fallback={<PageFallback />}><JournalPage /></Suspense>} />
                <Route path="/journal/:slug" element={<Suspense fallback={<PageFallback />}><JournalPostPage /></Suspense>} />
                <Route path="/gallery" element={<Suspense fallback={<PageFallback />}><GalleryPage /></Suspense>} />
                <Route path="/contact" element={<Suspense fallback={<PageFallback />}><ContactPage /></Suspense>} />
                <Route path="/getting-here" element={<Suspense fallback={<PageFallback />}><GettingHerePage /></Suspense>} />
                <Route path="/offers" element={<Suspense fallback={<PageFallback />}><OffersPage /></Suspense>} />
                <Route path="*" element={<Suspense fallback={<PageFallback />}><NotFoundPage /></Suspense>} />
              </Route>
              <Route path="/admin/login" element={<Suspense fallback={<AdminFallback />}><AdminLogin /></Suspense>} />
              <Route path="/admin" element={<Suspense fallback={<AdminFallback />}><AdminLayout /></Suspense>}>
                <Route index element={<Suspense fallback={null}><AdminDashboard /></Suspense>} />
                <Route path="settings/*" element={<Suspense fallback={null}><AdminSettings /></Suspense>} />
                <Route path="hero" element={<Suspense fallback={null}><AdminHero /></Suspense>} />
                <Route path="pages" element={<Suspense fallback={null}><AdminPages /></Suspense>} />
                <Route path="pages/:slug" element={<Suspense fallback={null}><AdminPageEditor /></Suspense>} />
                <Route path="rooms" element={<Suspense fallback={null}><AdminRooms /></Suspense>} />
                <Route path="dining" element={<Suspense fallback={null}><AdminDining /></Suspense>} />
                <Route path="experiences" element={<Suspense fallback={null}><AdminExperiences /></Suspense>} />
                <Route path="journal" element={<Suspense fallback={null}><AdminJournal /></Suspense>} />
                <Route path="gallery" element={<Suspense fallback={null}><AdminGallery /></Suspense>} />
                <Route path="reviews" element={<Suspense fallback={null}><AdminReviews /></Suspense>} />
                <Route path="promos" element={<Suspense fallback={null}><AdminPromos /></Suspense>} />
                <Route path="messages" element={<Suspense fallback={null}><AdminMessages /></Suspense>} />
                <Route path="getting-here" element={<Suspense fallback={null}><AdminGettingHere /></Suspense>} />
                <Route path="users" element={<Navigate to="/admin/settings/users" replace />} />
                <Route path="links" element={<Navigate to="/admin/settings/links" replace />} />
                <Route path="offers" element={<Suspense fallback={null}><AdminOffers /></Suspense>} />
                <Route path="subscribers" element={<Suspense fallback={null}><AdminSubscribers /></Suspense>} />
              </Route>
              <Route path="/login" element={<Navigate to="/admin/login" replace />} />
            </Routes>
          </SiteProvider>
        </AuthProvider>
      </BrowserRouter>
      <Toaster position="top-right" richColors closeButton />
    </div>
  );
}

export default App;
